import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdPhsInfoService, PhsCategoryInfo, PhsCategoryLabels, PhsCognitionGroupLabel, PhsColorInfo, PhsSharedLabels } from '../hd-phs-info.service';
import { PHS_CATEGORIES, PhsColorNumber, PhsToneNumber, phsToneIsLeft } from '../hd-phs-metadata';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { HdColorShapeComponent } from '../planet-grid/hd-color-shape/hd-color-shape.component';
import { getHdPlanetDisplayConfig } from '../planet-grid/hd-planet-display-config';
import { HdToneScaleComponent } from '../planet-grid/hd-tone-scale/hd-tone-scale.component';
import { HdToneShapeComponent } from '../planet-grid/hd-tone-shape/hd-tone-shape.component';
import { HdTransformationArrowComponent } from '../planet-grid/hd-transformation-arrow/hd-transformation-arrow.component';
import { HdPhsActivationInfoContext } from './hd-phs-activation-info-context.model';

/** Eén planeet-label voor de header (glyph + vertaalde naam in actieve content-taal). */
interface PhsPlanetLabel {
  readonly glyph: string;
  readonly name: string;
}

/** Header view-model — categorie-titel, side, planeten, positie, tagline. */
interface PhsHeaderViewModel {
  readonly title: string;
  readonly titleAlt: string;
  readonly sideLabel: string;
  readonly position: string;
  readonly planetA: PhsPlanetLabel;
  readonly planetB: PhsPlanetLabel;
  readonly tagline: string;
  readonly intro: string;
  readonly helper: string;
}

/**
 * Uitgelicht pijlblok bovenaan het paneel: wat de aangeklikte pijl zegt (bv.
 * "Actief brein" met "gefocust, nood aan voeding, eet meer"), groot en eerst.
 */
interface PhsHeroArrowViewModel {
  /** Categorie-label van de pijl (Brein / Omgeving stijl / Perspectief / Bewustzijn). */
  readonly label: string;
  readonly name: string;
  readonly short: string;
  /** Waar het vandaan komt: de toon die de pijlrichting bepaalt. */
  readonly source: string;
}

/**
 * Eén kaartje onder het pijlblok: de variabele zelf (Determinatie · Kalm), het
 * traject of de toon fixing, en de cognitie (Cognitie · Smaak), telkens met de
 * keynote en waar de waarde vandaan komt (kleur, toon).
 */
interface PhsHeroCardViewModel {
  readonly key: 'variable' | 'tone' | 'cognition';
  readonly label: string;
  readonly value: string;
  readonly short: string;
  readonly icon: string;
  readonly source: string;
}

/**
 * Strip "Wat dit zo maakt": de drager (Design Zon, Persoonlijkheid Noordknoop)
 * met poort.lijn, kleur, toon en base. Poort, lijn en base enkel wanneer de
 * context ze meekreeg.
 */
interface PhsOriginViewModel {
  readonly label: string;
  readonly carrier: string;
  readonly glyph: string;
  readonly gateLine: string;
  readonly colorText: string;
  readonly toneText: string;
  readonly baseText: string;
}

/** Kleur-sectie view-model — geactiveerde kleur inclusief groep-context. */
interface PhsColorSectionViewModel {
  readonly number: number;
  readonly name: string;
  readonly gloss: string;
  readonly short: string;
  readonly body: string;
  readonly icon: string;
  readonly groupName: string;
  readonly groupKeynote: string;
  readonly groupRange: string;
}

/** Toon-sectie view-model — geactiveerde toon-variant + cognitie + brein-pool. */
interface PhsToneSectionViewModel {
  readonly number: number;
  readonly variantName: string;
  readonly variantGloss: string;
  readonly variantShort: string;
  readonly variantBody: string;
  readonly variantIcon: string;
  readonly cognitionName: string;
  readonly cognitionShort: string;
  /** Uitleg van de cognitie/resonantie/het gevoel dat de toon bepaalt. */
  readonly cognitionBody: string;
  readonly cognitionIcon: string;
  readonly cognitionGroupName: string;
  readonly cognitionGroupTone: string;
  readonly brainPoleName: string;
  readonly brainPoleShort: string;
  /** Uitleg van de pijlrichting (brein/omgeving stijl/perspectief/bewustzijn). */
  readonly brainPoleBody: string;
  readonly brainPoleIcon: string;
  readonly brainPoleLabel: string;
}

/** Partner-sectie view-model (enkel personality, anders `null`). */
interface PhsPartnerSectionViewModel {
  readonly label: string;
  readonly thisName: string;
  readonly thisNumber: number;
  readonly partnerName: string;
  readonly partnerNumber: number;
}

/**
 * Traject-sectie view-model: het tegenovergestelde toon-polair-keyword met
 * een pijl naar het huidige (bv. "Volger → Leider" voor visie toon 3-links).
 * Enkel personality (visie / motivatie) — design tonen verschuiven niet via
 * een evolutie-as.
 */
interface PhsTrajectorySectionViewModel {
  readonly label: string;
  readonly fromName: string;
  readonly toName: string;
  /** Traject van deze kleur in woorden. */
  readonly body: string;
  /** Algemene uitleg van hoe het links/rechts-traject werkt. */
  readonly intro: string;
}

/** Blok "Het niet-fysieke": hoe de motivatiekleur een ontmoeting met het niet-fysieke ervaart. */
interface PhsNonPhysicalSectionViewModel {
  readonly label: string;
  /** Wat deze kleur ervaart (engel, demon, geest, god, entiteit, alien). */
  readonly body: string;
  /** Algemene uitleg: bundels, overdracht, en hoe je toetst of iets je dient. */
  readonly intro: string;
}

/**
 * Eén rij in de samenvattings-tabel bovenaan het info-panel: een
 * categorie-label (Determinatie / Cognitie / Brein / Binair) met de
 * geactiveerde waarde. `rightSub` staat subtiel direct na de waarde
 * (sub-label / context); `hint` komt — wanneer aanwezig — als 2e regel
 * eronder en wordt enkel bij de brein-rij gevuld (pijl-richting-uitleg).
 */
interface PhsSummaryRow {
  readonly key:
    | 'variable'
    | 'tone-fixing'
    | 'trajectory'
    | 'cognition'
    | 'partner-extra'
    | 'brain'
    | 'binary';
  readonly label: string;
  readonly value: string;
  readonly rightSub: string;
  readonly hint: string;
}

/**
 * Inhoud van het PHS-activatie info-panel: één geklikte kleur/toon-activatie
 * uit de planet-grid (zon/aarde of noordknoop/zuidknoop), met de bijhorende
 * vertaalde info uit {@link HdPhsInfoService}.
 *
 * Werkt als **content-component** — krijgt zijn context via input zodat hij
 * herbruikbaar is buiten een floating-panel context. Wanneer hij door
 * {@link HdPhsActivationInfoPanelComponent} (de floating-panel wrapper)
 * gemount wordt, leest die wrapper het `FLOATING_PANEL_DATA`-signaal uit en
 * zet het object als input op deze component.
 *
 * Spiegelt de styling-tokens van {@link HdPhsTransformationBandComponent}
 * (cheat-sheet band) — accent-rood voor design, zwart voor personality,
 * amber-cijfers, groene toon-accent — zodat de visuele taal consistent blijft
 * over chart, cheat-sheet en info-panel.
 */
@Component({
  selector: 'hd-phs-activation-info',
  templateUrl: './hd-phs-activation-info.component.html',
  styleUrl: './hd-phs-activation-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, HdColorShapeComponent, HdToneScaleComponent, HdToneShapeComponent, HdTransformationArrowComponent],
  host: {
    '[class.is-design]': "side() === 'design'",
    '[class.is-personality]': "side() === 'personality'",
    '[class.is-floating]': 'isFloating',
  },
})
export class HdPhsActivationInfoComponent {
  private readonly phsInfo = inject(HdPhsInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /**
   * `null` wanneer de component buiten een gedockte tab gemount is — dan
   * gedraagt de styling zich als floating dialog (compactere padding).
   */
  private readonly dockRenderZone = inject(DOCK_RENDER_ZONE, { optional: true });
  protected readonly isFloating = this.dockRenderZone == null;

  readonly context = input.required<HdPhsActivationInfoContext>();

  protected readonly category = computed(() => this.context().category);
  protected readonly side = computed(() => this.context().side);
  protected readonly color = computed<PhsColorNumber>(() => this.clampColor(this.context().color));
  protected readonly tone = computed<PhsToneNumber>(() => this.clampTone(this.context().tone));
  protected readonly colorConfig = computed(() => this.context().colorConfig);

  constructor() {
    // HdPhsInfoService laadt scopes alleen voor de globale app-taal (transloco
    // langChanges$). De chart-pagina kan echter een per-pagina content-taal-
    // override hebben — als die afwijkt zou de scope hier ongeladen blijven en
    // alle vertaalde velden (tagline, group, brain, color, tone, cognition)
    // lege strings teruggeven. Triggert de load expliciet voor onze actieve
    // content-taal, idempotent omdat transloco caching doet.
    effect(() => {
      this.phsInfo.loadScopesForLang(this.lang());
    });
  }

  /**
   * Side-accent voor het tone-scale-pijltje — design = rood, personality =
   * zwart (volgt `colorToneShape.{design|personality}`). Spiegelt dezelfde
   * accent-keuze als de chart-block waarop geklikt is.
   */
  protected readonly toneScaleAccent = computed(() => {
    const shape = this.colorConfig().colorToneShape;
    return this.side() === 'design' ? shape.design : shape.personality;
  });

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  private readonly altLang = computed(() => (this.lang() === 'en' ? 'nl' : 'en'));
  private readonly isNl = computed(() => this.lang() !== 'en');

  private readonly info = computed<PhsCategoryInfo>(() => this.phsInfo.categoryInfoIn(this.category(), this.lang()));
  private readonly infoAlt = computed<PhsCategoryInfo>(() => this.phsInfo.categoryInfoIn(this.category(), this.altLang()));
  private readonly meta = computed(() => PHS_CATEGORIES[this.category()]);

  /** Gedeelde labels (kleur-/toon-woord, side-naam, …) in actieve content-taal. */
  protected readonly sharedLabels = computed<PhsSharedLabels>(() => this.phsInfo.sharedLabelsIn(this.lang()));
  /** Categorie-labels (variabele-naam, brein-label, sub-labels, hints). */
  protected readonly categoryLabels = computed<PhsCategoryLabels>(() =>
    this.phsInfo.categoryLabelsIn(this.category(), this.lang()),
  );

  /** Categorie-titel, side, planeten, positie, tagline, helper. */
  protected readonly header = computed<PhsHeaderViewModel>(() => {
    const lang = this.lang();
    const altLang = this.altLang();
    const nl = this.isNl();
    const labels = this.phsInfo.categoryLabelsIn(this.category(), lang);
    const labelsAlt = nl ? this.phsInfo.categoryLabelsIn(this.category(), altLang) : null;
    const shared = this.phsInfo.sharedLabelsIn(lang);
    const meta = this.meta();
    const planetCfgA = getHdPlanetDisplayConfig(meta.planets.a);
    const planetCfgB = getHdPlanetDisplayConfig(meta.planets.b);
    const planetNameA = this.planetInfo.planetInfoIn(meta.planets.a, lang).name;
    const planetNameB = this.planetInfo.planetInfoIn(meta.planets.b, lang).name;
    return {
      // Hoofdtitel = transformatie-naam (Vertering / Digestion), niet de
      // variabele-naam — die wordt al getoond als label in de summary-tabel
      // ("DETERMINATIE | Kalm"). Hier toont de header de overkoepelende
      // transformatie waar de variabele toe behoort.
      title: labels.processName || labels.variableName,
      titleAlt: labelsAlt?.processName ?? '',
      sideLabel: meta.side === 'design' ? shared.side.design : shared.side.personality,
      position: labels.position,
      planetA: { glyph: planetCfgA.astronomiconChar, name: planetNameA },
      planetB: { glyph: planetCfgB.astronomiconChar, name: planetNameB },
      tagline: this.info().tagline,
      intro: this.info().intro,
      helper: this.info().helper,
    };
  });

  /** Geactiveerde kleur — inclusief groep (1-3 of 4-6) en optionele EN-gloss in NL-modus. */
  protected readonly colorSection = computed<PhsColorSectionViewModel>(() => {
    const color = this.color();
    const colorInfo = this.info().colors[color - 1];
    const colorInfoAlt = this.isNl() ? this.infoAlt().colors[color - 1] : null;
    const inLowGroup = color <= 3;
    const group = inLowGroup ? this.info().group.low : this.info().group.high;
    return {
      number: color,
      name: colorInfo?.name ?? '',
      gloss: this.glossFor(colorInfo?.name, colorInfoAlt?.name),
      short: colorInfo?.short ?? '',
      body: colorInfo?.body ?? '',
      icon: colorInfo?.icon ?? '',
      groupName: group.name,
      groupKeynote: group.keynote,
      groupRange: inLowGroup ? '1-3' : '4-6',
    };
  });

  /** Geactiveerde toon — variant-naam + cognitie + brein-pool. */
  protected readonly toneSection = computed<PhsToneSectionViewModel>(() => {
    const color = this.color();
    const tone = this.tone();
    const colorInfo = this.info().colors[color - 1] as PhsColorInfo | undefined;
    const colorInfoAlt = this.isNl() ? this.infoAlt().colors[color - 1] : null;
    const isLeftPole = phsToneIsLeft(tone);
    const variant = isLeftPole ? colorInfo?.tone.left : colorInfo?.tone.right;
    const variantAlt = isLeftPole ? colorInfoAlt?.tone.left : colorInfoAlt?.tone.right;
    const variantIcon = isLeftPole ? colorInfo?.toneLeftIcon : colorInfo?.toneRightIcon;
    const cognition = this.info().cognition[tone - 1];
    const cognitionGroup = this.cognitionGroupFor(tone);
    const brainPole = isLeftPole ? this.info().brain.left : this.info().brain.right;
    const variantShort = variant?.short ?? '';
    return {
      number: tone,
      variantName: variant?.name ?? '',
      variantGloss: this.glossFor(variant?.name, variantAlt?.name),
      variantShort,
      variantBody: this.bodyBeyondShort(variant?.body ?? '', variantShort),
      variantIcon: variantIcon ?? '',
      cognitionName: cognition?.name ?? '',
      cognitionShort: cognition?.short ?? '',
      cognitionBody: cognition?.body ?? '',
      cognitionIcon: cognition?.icon ?? '',
      cognitionGroupName: cognitionGroup?.name ?? '',
      cognitionGroupTone: cognitionGroup?.tone ?? '',
      brainPoleName: brainPole.name,
      brainPoleShort: brainPole.short,
      brainPoleBody: brainPole.body,
      brainPoleIcon: brainPole.icon,
      brainPoleLabel: isLeftPole ? '1-3' : '4-6',
    };
  });

  /**
   * Uitgelicht pijlblok bovenaan: de keynote van de aangeklikte pijl (zelfde
   * woorden als de PHS-cheat-sheet), groot en eerst, met de toon als herkomst.
   */
  protected readonly heroArrow = computed<PhsHeroArrowViewModel>(() => {
    const tone = this.toneSection();
    const labels = this.categoryLabels();
    const toneRef = `${this.sharedLabels().toneLabel} ${tone.number}`;
    return {
      label: labels.brainLabel,
      name: tone.brainPoleName,
      short: tone.brainPoleShort,
      source: labels.brainPoleHint ? `${toneRef} · ${labels.brainPoleHint}` : toneRef,
    };
  });

  /**
   * Kaartjes onder het pijlblok, per categorie:
   *
   *  - **Determinatie**: de variabele is het toonwoord van de kleur ("Kalm" =
   *    kleur Aanraking, linkertoon), dus één variabele-kaart plus cognitie.
   *  - **Omgeving**: de kleur ("Grotten") plus de toon fixing door de Nodes.
   *  - **Visie / Motivatie**: de kleur ("Verlangen") plus het traject
   *    ("Leider"; de volle "Volger → Leider" staat in de traject-sectie).
   *
   * Elke kaart noemt waar de waarde vandaan komt (kleur en/of toon).
   */
  protected readonly heroCards = computed<readonly PhsHeroCardViewModel[]>(() => {
    const category = this.category();
    const labels = this.categoryLabels();
    const shared = this.sharedLabels();
    const color = this.colorSection();
    const tone = this.toneSection();
    const colorRef = `${shared.colorLabel} ${color.number}`;
    const toneRef = `${shared.toneLabel} ${tone.number}`;
    const cards: PhsHeroCardViewModel[] = [];
    if (category === 'determination') {
      cards.push({
        key: 'variable',
        label: labels.variableName,
        value: tone.variantName,
        short: tone.variantShort,
        icon: tone.variantIcon,
        source: `${colorRef} (${color.name}) · ${toneRef}`,
      });
    } else {
      cards.push({
        key: 'variable',
        label: labels.variableName,
        value: color.name,
        short: color.short,
        icon: color.icon,
        source: colorRef,
      });
      cards.push({
        key: 'tone',
        label: category === 'environment' ? shared.nodalToneFixingLabel : shared.trajectoryLabel,
        value: tone.variantName,
        short: tone.variantShort,
        icon: tone.variantIcon,
        source: toneRef,
      });
    }
    cards.push({
      key: 'cognition',
      label: labels.cognitionLabel,
      value: tone.cognitionName,
      short: tone.cognitionShort,
      icon: tone.cognitionIcon,
      source: toneRef,
    });
    return cards;
  });

  /** Strip "Wat dit zo maakt": drager, poort.lijn, kleur, toon en base van deze activatie. */
  protected readonly origin = computed<PhsOriginViewModel>(() => {
    const context = this.context();
    const header = this.header();
    const shared = this.sharedLabels();
    const color = this.colorSection();
    return {
      label: shared.originLabel,
      carrier: `${header.sideLabel} ${header.planetA.name}`,
      glyph: header.planetA.glyph,
      gateLine: context.gate != null && context.line != null ? `${context.gate}.${context.line}` : '',
      colorText: color.name ? `${shared.colorLabel} ${color.number} · ${color.name}` : `${shared.colorLabel} ${color.number}`,
      toneText: `${shared.toneLabel} ${this.tone()}`,
      baseText: context.base != null ? `${shared.baseLabel} ${context.base}` : '',
    };
  });

  /**
   * Overzichtstabel net boven de kleur-details (herhaling van alles op één
   * plek, wens gebruiker 2026-11-03) — opgebouwd per categorie:
   *
   *  - **Determinatie** (design+incarnation): variabele-rij toont de
   *    toon-variant ("Kalm"); geen extra rij eronder.
   *  - **Omgeving** (design+life): variabele-rij toont de kleur-naam
   *    ("Caves"); extra rij "Toon fixing" eronder met de toon-variant
   *    ("Blending") — de Nodes fixeren de toon-polariteit.
   *  - **Visie / Motivatie** (personality): variabele-rij toont de
   *    kleur-naam ("Verlangen"); extra rij "Traject/Trajectory" met enkel
   *    de huidige toon-variant ("Leider" — de volle "Volger → Leider"
   *    staat onderaan in de detail-sectie). Onder cognitie komt een extra
   *    rij met de partner-naam, gelabeld "Afleiding/Distraction" voor
   *    visie en "Overdracht/Transference" voor motivatie.
   *
   * De vaste staartrijen (brein → binair) blijven gelijk voor alle 4
   * categorieën — brein heeft als enige een hint-zin als 2e regel
   * (pijl-richting bepaald door toon).
   */
  protected readonly summaryRows = computed<readonly PhsSummaryRow[]>(() => {
    const category = this.category();
    const labels = this.categoryLabels();
    const shared = this.sharedLabels();
    const info = this.info();
    const color = this.colorSection();
    const tone = this.toneSection();
    const rows: PhsSummaryRow[] = [];

    // 1. Variabele-rij — voor determinatie de toon-variant, voor de andere
    //    drie categorieën de kleur-naam (volgt de HD-spec waarbij omgeving /
    //    visie / motivatie primair via de kleur worden geïdentificeerd, en
    //    determinatie via zijn toon-polair-keyword).
    rows.push({
      key: 'variable',
      label: labels.variableName,
      value: category === 'determination' ? tone.variantName : color.name,
      rightSub: labels.subLabelLong,
      hint: '',
    });

    // 2. Toon-context-rij onder de variabele — alleen relevant voor
    //    categorieën waarbij de variabele-rij de kleur-naam toont.
    if (category === 'environment') {
      rows.push({
        key: 'tone-fixing',
        label: shared.nodalToneFixingLabel,
        value: tone.variantName,
        rightSub: '',
        hint: '',
      });
    } else if (category === 'view' || category === 'motivation') {
      rows.push({
        key: 'trajectory',
        label: shared.trajectoryLabel,
        value: tone.variantName,
        rightSub: '',
        hint: '',
      });
    }

    // 3. Cognitie-rij.
    rows.push({
      key: 'cognition',
      label: labels.cognitionLabel,
      value: tone.cognitionName,
      rightSub: labels.subLabelLong,
      hint: '',
    });

    // 4. Partner-rij — alleen personality (visie = Afleiding/Distraction,
    //    motivatie = Overdracht/Transference). Toont enkel de partner-
    //    kleur-naam; de volle N ↔ N+3 staat in de partner-detail-sectie.
    if (info.dynamic) {
      const partner = this.partnerSection();
      if (partner) {
        rows.push({
          key: 'partner-extra',
          label: partner.label,
          value: partner.partnerName,
          rightSub: '',
          hint: '',
        });
      }
    }

    // 5. Brein-rij (gemeenschappelijk).
    const brainValue = this.stripTrailingLabel(tone.brainPoleName, labels.brainLabel);
    rows.push({
      key: 'brain',
      label: labels.brainLabel,
      value: brainValue,
      rightSub: labels.subLabelShort,
      hint: labels.brainPoleHint,
    });

    // 6. Binair-rij (gemeenschappelijk) — cognitie-groep + toon-karakter.
    const binaryValue = [tone.cognitionGroupName, tone.cognitionGroupTone]
      .filter(part => part && part.length > 0)
      .join(' / ');
    if (binaryValue) {
      rows.push({
        key: 'binary',
        label: shared.binaryLabel,
        value: binaryValue,
        rightSub: '',
        hint: '',
      });
    }

    return rows;
  });

  /**
   * Strip een redundant label-suffix uit een waarde — bv. brain-pool "Actief
   * brein" naast label "Brein" → "Actief". Case-insensitief; geen match →
   * waarde blijft ongewijzigd (bv. environment "Geobserveerd" naast
   * "Omgeving stijl").
   */
  private stripTrailingLabel(value: string, label: string): string {
    if (!value || !label) return value;
    const suffix = ' ' + label.toLowerCase();
    if (value.toLowerCase().endsWith(suffix)) {
      return value.slice(0, value.length - suffix.length).trim();
    }
    return value;
  }

  /** Personality-only partner (N ↔ N+3). Voor design-zijde `null`. */
  protected readonly partnerSection = computed<PhsPartnerSectionViewModel | null>(() => {
    const info = this.info();
    if (!info.dynamic) return null;
    const color = this.color();
    const colorInfo = info.colors[color - 1];
    if (!colorInfo?.partnerColor) return null;
    const partner = info.colors[colorInfo.partnerColor - 1];
    if (!partner) return null;
    const labels = this.phsInfo.categoryLabelsIn(this.category(), this.lang());
    return {
      label: labels.partnerLabel,
      thisName: colorInfo.name,
      thisNumber: color,
      partnerName: partner.name,
      partnerNumber: partner.number,
    };
  });

  /**
   * Personality-only traject (evolutie-as): toon de tegenovergestelde
   * polariteit met een pijl naar het huidige keyword (bv. "Volger → Leider"
   * voor visie toon 3-links). Voor design-zijde `null` — daar zijn de tonen
   * niet evoluerend.
   */
  protected readonly trajectorySection = computed<PhsTrajectorySectionViewModel | null>(() => {
    const info = this.info();
    if (!info.dynamic) return null;
    const color = this.color();
    const tone = this.tone();
    const colorInfo = info.colors[color - 1];
    if (!colorInfo) return null;
    const isLeftPole = phsToneIsLeft(tone);
    const current = isLeftPole ? colorInfo.tone.left : colorInfo.tone.right;
    const opposite = isLeftPole ? colorInfo.tone.right : colorInfo.tone.left;
    if (!current?.name || !opposite?.name) return null;
    return {
      label: this.sharedLabels().trajectoryLabel,
      fromName: opposite.name,
      toName: current.name,
      body: colorInfo.trajectory,
      intro: info.trajectoryIntro,
    };
  });

  /**
   * Enkel Motivation: hoe deze kleur het niet-fysieke ervaart (Rave Cosmology VI).
   * `null` voor de andere variabelen, die geen tekst voor deze laag hebben.
   */
  protected readonly nonPhysicalSection = computed<PhsNonPhysicalSectionViewModel | null>(() => {
    const info = this.info();
    const body = info.colors[this.color() - 1]?.nonPhysical ?? '';
    if (!body) return null;
    return { label: info.nonPhysicalLabel, body, intro: info.nonPhysicalIntro };
  });

  /**
   * Cognitie-systeemgroep voor de gegeven toon: tonen 1-2 = groep 0 (Milt),
   * 3-4 = groep 1 (Gedachten), 5-6 = groep 2 (Emotie). Maps op de
   * 3-cognitionGroups-array uit {@link HdPhsInfoService.sharedLabels}.
   */
  private cognitionGroupFor(tone: PhsToneNumber): PhsCognitionGroupLabel | null {
    const groups = this.phsInfo.sharedLabelsIn(this.lang()).cognitionGroups;
    const idx = Math.floor((tone - 1) / 2);
    return groups[idx] ?? null;
  }

  /**
   * EN-gloss naast NL-naam: enkel in NL-modus en alleen wanneer de twee
   * vertalingen daadwerkelijk verschillen — anders ruis.
   */
  private glossFor(primary: string | undefined, alt: string | undefined): string {
    if (!this.isNl() || !primary || !alt) return '';
    return alt.toLowerCase() === primary.toLowerCase() ? '' : alt;
  }

  /**
   * Bij sommige personality-tonen zegt de rapporttekst niets meer dan de
   * keynote zelf (bv. Volger: "Je eigen pad volgen"); dan tonen we de body
   * niet om dezelfde zin niet twee keer onder elkaar te zetten.
   */
  private bodyBeyondShort(body: string, short: string): string {
    const normalize = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
    return body && normalize(body) === normalize(short) ? '' : body;
  }

  private clampColor(value: number): PhsColorNumber {
    const v = Math.min(6, Math.max(1, Math.floor(value)));
    return v as PhsColorNumber;
  }

  private clampTone(value: number): PhsToneNumber {
    const v = Math.min(6, Math.max(1, Math.floor(value)));
    return v as PhsToneNumber;
  }
}
